import React from "react";
import { components } from "react-select";
import styled from "styled-components";
import {
  FontWeight,
  Radius,
  background,
  border,
  borderRadius,
  dominant,
  form,
  message,
  transparentize,
} from "@glossy";
import { GlossyTheme } from "@glossy/theme/types";
import Icon from "@ui-kit/icon";

export const SIZES = {
  s: { height: 32, radius: Radius.Small },
  m: { height: 36, radius: Radius.Medium },
  l: { height: 48, radius: Radius.MediumLarge },
} as const;
export type Size = keyof typeof SIZES;

export const VARIANTS = ["primary", "secondary", "tertiary"] as const;
export type Variant = typeof VARIANTS[number];

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <Icon glyph="chevron-small-down" size="ms" />
  </components.DropdownIndicator>
);

const ClearCross = styled.div`
  padding: 8px;
  border-radius: 50%;
  color: ${form};
  line-height: 0;
  :hover {
    background-color: ${(props) =>
      transparentize(0.85, form(props, "primary"))};
  }
`;

const ClearIndicator = (props) => (
  <components.ClearIndicator {...props}>
    <ClearCross>
      <Icon glyph="cross-thick" size="xs" />
    </ClearCross>
  </components.ClearIndicator>
);

const MultiValueRemove = (props) => (
  <components.MultiValueRemove {...props}>
    <Icon glyph="cross-thick" size="xs" />
  </components.MultiValueRemove>
);

export const selectComponents = {
  DropdownIndicator,
  ClearIndicator,
  MultiValueRemove,
};

export const getCustomizedTheme =
  (theme: GlossyTheme, size: Size) => (provided) => {
    const props = { theme };
    const primary = (amount: number) => transparentize(amount, dominant(props));
    const danger = (amount: number) =>
      transparentize(amount, message(props, "error"));
    const neutral = (amount: number) =>
      transparentize(amount, form(props, "primary"));
    return {
      ...provided,
      borderRadius: SIZES[size].radius,
      colors: {
        primary: primary(0),
        primary75: primary(0.25),
        primary50: primary(0.5),
        primary25: primary(0.75),
        danger: danger(0),
        dangerLight: danger(0.75),
        neutral0: neutral(1),
        neutral5: neutral(0.95),
        neutral10: neutral(0.9),
        neutral20: neutral(0.8),
        neutral30: neutral(0.7),
        neutral40: neutral(0.6),
        neutral50: neutral(0.5),
        neutral60: neutral(0.4),
        neutral70: neutral(0.3),
        neutral80: neutral(0.2),
        neutral90: neutral(0.1),
      },
      spacing: {
        baseUnit: 1,
        controlHeight: SIZES[size].height,
        menuGutter: 6,
      },
    };
  };

export const getCustomStyles = (
  theme: GlossyTheme,
  size: Size,
  variant: Variant
) => {
  const props = { theme };
  return {
    control: (provided, state) => ({
      ...provided,
      opacity: state.isDisabled && 0.5,
      cursor: "pointer",
      color: form(props, "primary"),
      border: "none",
      transition: null,
      ...{
        primary: {
          backgroundColor: state.isFocused
            ? "transparent"
            : transparentize(0.85, form(props, "primary")),
          boxShadow: state.isFocused && `0 0 0 2px ${dominant(props)} inset`,
          ":hover": {
            backgroundColor:
              !state.isFocused && transparentize(0.8, form(props, "primary")),
          },
        },
        secondary: {
          boxShadow: state.isFocused
            ? `0 0 0 2px ${dominant(props)} inset`
            : `0 0 0 1px
    ${transparentize(0.5, form(props, "primary"))} inset`,
          ":hover": {
            boxShadow:
              !state.isFocused && `0 0 0 1px ${form(props, "primary")} inset`,
          },
        },
        tertiary: {
          borderRadius: 0,
          boxShadow: state.isFocused
            ? `0 -2px 0 0 ${dominant(props)} inset`
            : `0 -1px 0 0 ${transparentize(0.5, form(props, "primary"))} inset`,
          ":hover": {
            boxShadow:
              !state.isFocused && `0 -1px 0 0 ${form(props, "primary")} inset`,
          },
        },
      }[variant],
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: 2,
    }),
    placeholder: (provided) => ({
      ...provided,
      padding: "0 16px",
      color: transparentize(0.3, form(props, "primary")),
      fontStyle: "italic",
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused
        ? dominant(props)
        : transparentize(0.3, form(props, "primary")),
      padding: 6,
      marginRight: 6,
    }),
    indicatorSeparator: () => ({ display: "none" }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: 0,
      width: 20,
      marginRight: 4,
    }),
    multiValue: (provided) => ({
      ...provided,
      borderRadius: SIZES[size].radius,
      color: "inherit",
      margin: 2,
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      padding: "0 6px",
      color: "inherit",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      ...borderRadius("right", SIZES[size].radius),
      padding: "0 6px 0 5px",
      textAlign: "center",
    }),
    singleValue: (provided) => ({
      ...provided,
      padding: "0 13px",
      color: "inherit",
    }),
    input: (provided) => ({
      ...provided,
      padding: "0 16px",
      color: "inherit",
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      maxHeight: SIZES[size].height * 8,
    }),
    menu: (provided) => ({
      ...provided,
      border: `1px solid ${border(props)}`,
      boxShadow: null,
      borderRadius: SIZES[size].radius,
      backgroundColor: background(props),
      margin: "6px 0",
    }),
    menuPortal: (provided) => ({ ...provided, zIndex: 1050 }),
    option: (provided, state) => {
      const LINE_HEIGHT = 18;
      return {
        ...provided,
        cursor: "pointer",
        lineHeight: `${LINE_HEIGHT}px`,
        padding: `${(SIZES[size].height - LINE_HEIGHT) / 2}px 16px`,
        fontWeight: state.isSelected && FontWeight.Bold,
        color: form(props, state.isSelected ? "include" : "primary"),
        backgroundColor:
          state.isSelected && transparentize(0.8, form(props, "include")),
        "&:hover": {
          backgroundColor: transparentize(0.85, form(props, "primary")),
          color: form(props, "primary"),
        },
      };
    },
  };
};
